import { apiClient } from "@/lib/api-client";
import { VideoInterviewData } from "../dto/videoInterviewDto";
import { AxiosError } from "axios";
import { HttpStatusCodes } from "@/lib/http-status-codes";
import { MAX_UPLOAD_MB } from "../constants/videoInterviewLimits";

export type VideoUploadErrorKind = "size" | "network" | "unknown";
export interface VideoUploadError {
    kind: VideoUploadErrorKind;
    message: string;
}

const PERCENT = 100;
const DEFAULT_UPLOAD_ERROR = "Failed to upload video";

export function isVideoUploadError(error: unknown): error is VideoUploadError {
    return (
        typeof error === "object" &&
        error !== null &&
        "kind" in error &&
        "message" in error
    );
}

export const SIZE_ERROR_MESSAGE = `Your video is larger than the ${MAX_UPLOAD_MB} MB limit. Please record again.`;

function mapUploadError(error: unknown): VideoUploadError {
    if (!(error instanceof AxiosError)) {
        return { kind: "unknown", message: DEFAULT_UPLOAD_ERROR };
    }
    if (error.response?.status === HttpStatusCodes.PAYLOAD_TOO_LARGE) {
        return { kind: "size", message: SIZE_ERROR_MESSAGE };
    }

    if (error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ETIMEDOUT || !error.response) {
        return {
            kind: "network",
            message: "Upload failed. Please check your connection and try again."
        };
    }

    const data = error.response.data as { error?: string; message?: string } | undefined;
    const backendMessage = data?.error || data?.message;

    return {
        kind: "unknown",
        message: backendMessage || DEFAULT_UPLOAD_ERROR
    };
}

const MP4_MIME_TYPE = "video/mp4";
const WEBM_MIME_TYPE = "video/webm";

function getBaseMimeType(mimeType: string): string {
    return mimeType.startsWith(MP4_MIME_TYPE) ? MP4_MIME_TYPE : WEBM_MIME_TYPE;
}

function getFileExtension(mimeType: string): string {
    return mimeType === MP4_MIME_TYPE ? "mp4" : "webm";
}

export const GetVideoInterviewData = async (token: string): Promise<VideoInterviewData> => {
    const response = await apiClient.get<VideoInterviewData>(`/interview/public/${token}`);
    return response.data;
};

export const UploadInterviewVideo = async (
  token: string,
  videoBlob: Blob,
  captchaToken: string = "",
  onUploadProgress?: (percent: number) => void,
): Promise<boolean> => {
  const formData = new FormData();
  const baseType = getBaseMimeType(videoBlob.type);
  const file = new File([videoBlob], `interview.${getFileExtension(baseType)}`, { type: baseType });
  formData.append("file", file);
  formData.append("captchaToken", captchaToken);

  try {
    await apiClient.post(`/interview/public/${token}/video`, formData, {
      onUploadProgress: (event) => {
        if (onUploadProgress && event.total) {
          onUploadProgress(Math.round((event.loaded * PERCENT) / event.total));
        }
      },
    });
    return true;
  } catch (error: unknown) {
    throw mapUploadError(error);
  }
};
